import React, { useState } from 'react';
import { Button } from '../ui/button';
import { 
  Search, 
  Filter, 
  X, 
  RefreshCw,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Tag,
  CheckCircle,
  Package
} from 'lucide-react';

export const AdvancedPlanSearch = ({ onSearch, onClear, isSearching = false }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchParams, setSearchParams] = useState({
    text: '',
    is_public: null,
    is_most_popular: null,
    min_price: '',
    max_price: '',
    features: [],
    sort_by: 'display_order',
    sort_direction: 'asc'
  });

  const availableFeatures = [
    'ai_insights',
    'gift_planning',
    'social_radar', 
    'advanced_analytics',
    'priority_support',
    'white_label',
    'api_access',
    'custom_fields',
    'team_collaboration',
    'data_export'
  ];

  const handleInputChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeatureToggle = (feature) => {
    setSearchParams(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handleSearch = () => {
    // Clean up search params
    const cleanParams = { ...searchParams };
    
    // Remove empty values
    Object.keys(cleanParams).forEach(key => {
      if (cleanParams[key] === '' || cleanParams[key] === null) {
        delete cleanParams[key];
      }
    });
    
    // Convert price strings to numbers
    if (cleanParams.min_price) cleanParams.min_price = parseFloat(cleanParams.min_price);
    if (cleanParams.max_price) cleanParams.max_price = parseFloat(cleanParams.max_price);
    
    onSearch(cleanParams);
  };

  const handleClear = () => {
    setSearchParams({
      text: '',
      is_public: null,
      is_most_popular: null,
      min_price: '',
      max_price: '',
      features: [],
      sort_by: 'display_order',
      sort_direction: 'asc'
    });
    onClear();
  };

  const hasActiveFilters = 
    searchParams.text ||
    searchParams.is_public !== null ||
    searchParams.is_most_popular !== null ||
    searchParams.min_price ||
    searchParams.max_price ||
    searchParams.features.length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6 dark:bg-gray-900 dark:border-gray-700">
      {/* Basic Search */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search plans by name, description, or key..."
            value={searchParams.text}
            onChange={(e) => handleInputChange('text', e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        
        <Button
          onClick={() => setShowAdvanced(!showAdvanced)}
          variant="outline"
          size="sm"
          className={hasActiveFilters ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : ''}
        >
          <Filter className="w-4 h-4 mr-2" />
          Advanced
          {showAdvanced ? <ChevronDown className="w-4 h-4 ml-1" /> : <ChevronRight className="w-4 h-4 ml-1" />}
        </Button>
        
        <Button onClick={handleSearch} size="sm" disabled={isSearching}>
          {isSearching ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Searching...
            </>
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              Search
            </>
          )}
        </Button>
        
        {hasActiveFilters && (
          <Button onClick={handleClear} variant="outline" size="sm">
            <X className="w-4 h-4 mr-2" />
            Clear
          </Button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Status Filters */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Status Filters
              </h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="public_status"
                    checked={searchParams.is_public === null}
                    onChange={() => handleInputChange('is_public', null)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">All Plans</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="public_status"
                    checked={searchParams.is_public === true}
                    onChange={() => handleInputChange('is_public', true)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Public Only</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="public_status"
                    checked={searchParams.is_public === false}
                    onChange={() => handleInputChange('is_public', false)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Draft Only</span>
                </label>
                
                <label className="flex items-center mt-3">
                  <input
                    type="checkbox"
                    checked={searchParams.is_most_popular === true}
                    onChange={(e) => handleInputChange('is_most_popular', e.target.checked ? true : null)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Most Popular Only</span>
                </label>
              </div>
            </div>

            {/* Price Range */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Price Range (Monthly)
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Min Price</label>
                  <input
                    type="number"
                    value={searchParams.min_price}
                    onChange={(e) => handleInputChange('min_price', e.target.value)}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Max Price</label>
                  <input
                    type="number"
                    value={searchParams.max_price}
                    onChange={(e) => handleInputChange('max_price', e.target.value)}
                    placeholder="1000"
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Feature Filters */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Features
              </h4>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {availableFeatures.map(feature => (
                  <label key={feature} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={searchParams.features.includes(feature)}
                      onChange={() => handleFeatureToggle(feature)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                      {feature.replace(/_/g, ' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Sort Options */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Sort Options</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Sort By</label>
                <select
                  value={searchParams.sort_by}
                  onChange={(e) => handleInputChange('sort_by', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                >
                  <option value="display_order">Display Order</option>
                  <option value="name">Name</option>
                  <option value="created_at">Created Date</option>
                  <option value="pricing.monthly">Monthly Price</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Direction</label>
                <select
                  value={searchParams.sort_direction}
                  onChange={(e) => handleInputChange('sort_direction', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600 dark:text-gray-400">Active filters:</span>
            <div className="flex flex-wrap gap-1">
              {searchParams.text && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs dark:bg-indigo-900/20 dark:text-indigo-400">
                  Text: "{searchParams.text}"
                </span>
              )}
              {searchParams.is_public !== null && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs dark:bg-indigo-900/20 dark:text-indigo-400">
                  Status: {searchParams.is_public ? 'Public' : 'Draft'}
                </span>
              )}
              {searchParams.is_most_popular && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs dark:bg-indigo-900/20 dark:text-indigo-400">
                  Most Popular
                </span>
              )}
              {(searchParams.min_price || searchParams.max_price) && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs dark:bg-indigo-900/20 dark:text-indigo-400">
                  Price: ${searchParams.min_price || '0'} - ${searchParams.max_price || '∞'}
                </span>
              )}
              {searchParams.features.length > 0 && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs dark:bg-indigo-900/20 dark:text-indigo-400">
                  Features: {searchParams.features.length}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedPlanSearch;